/**
 * One case in Saker, the same five parts for every kind (prototype case-templates.prototype.html,
 * owner 2026-10-28 "good, implement", then the review of the same day): the state and what happened in one sentence; who and
 * when; the facts as a figure; why it matters with its law; at most three actions, one that settles
 * it, one that opens or contacts, and Ta senere. The adapter (reviewBarAdapter.ts) computes every
 * number; this file only draws. A kind without the data for its figure has no figure, only words.
 */
import { useUser } from '@/api/people';
import { T } from '@/i18n';
import { oslo, osloISO } from '@/rules';
import { useDirty } from '@/shell/context';
import { Tip } from '@/ui';
import { initials, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { type CSSProperties, type ReactNode, useEffect, useState } from 'react';
import css from './CaseDetail.module.css';
import rb from './ReviewBar.module.css';
import {
  type ActInput,
  CORRECTION_REASONS,
  type CaseAction,
  type CaseFigure,
  type CaseShift,
  type CorrectOut,
  type ReviewCase,
} from './reviewBar.types';
import { drafts } from './saker';

/** The figure's accent; the state's colour is the state's own (stateOf). */
const TONE: Record<ReviewCase['tone'], string> = {
  d: 'var(--dgr-l)',
  w: 'var(--warn-l)',
  i: 'var(--blue-l)',
};
const INK: Record<ReviewCase['tone'], string> = { d: 'var(--dgr-i)', w: 'var(--warn-i)', i: 'var(--blue-i)' };

export type StateKind = 'warning' | 'danger' | 'neutral' | 'ok';
/**
 * The word a case's state is said in, and its colour (prototype «Saker navigation redesign», owner
 * 2026-10-28): amber while it waits on the manager or must be cleared up, red for a rule warning,
 * grey while it waits on an employee or is put off, green when it is ready. Colour only for state.
 */
export function stateOf(c: ReviewCase, o: { waiting?: boolean; later?: boolean } = {}) {
  if (o.later) return { word: T('Utsatt'), kind: 'neutral' as StateKind };
  const word = c.status ?? (o.waiting ? T('Venter på ansatt') : T('Venter på deg'));
  const kind: StateKind =
    word === T('Regelvarsel') || word === T('Brudd')
      ? 'danger'
      : word === T('Klar til godkjenning')
        ? 'ok'
        : o.waiting || word === T('Venter på ansatt')
          ? 'neutral'
          : 'warning';
  return { word, kind };
}

type Correct = Extract<CaseAction, { kind: 'correct-in' | 'correct-out' }>;
/** what writes: hidden, with the reason said once, when the reader may not or cannot write now (S16, S18) */
const WRITES: CaseAction['kind'][] = [
  'decide',
  'correct-in',
  'correct-out',
  'approve',
  'pause',
  'plan',
  'cover',
];

export interface CaseDetailProps {
  c: ReviewCase;
  busy: boolean;
  error: string | null;
  titleRef: React.RefObject<HTMLHeadingElement>;
  /** the case's date is outside the period on screen: its tag moves the plan there */
  outside: boolean;
  /** waiting on an employee: shown, never worked here */
  waiting: boolean;
  /** put off with Ta senere: the button brings it back instead */
  later: boolean;
  /** the day it was put off to, YYYY-MM-DD */
  laterUntil?: string | undefined;
  /** the reader may write here now: online and allowed (S16, S17) */
  canWrite?: boolean | undefined;
  readOnly?: boolean | undefined;
  impact: ReactNode;
  extra: ReactNode;
  about: ReactNode;
  onPerson: (id: number) => void;
  onDay: (date: string) => void;
  onShowPeriod: () => void;
  onAction: (a: CaseAction) => void;
  onSubmit: (a: Correct, input: ActInput) => void;
  onLater: () => void;
  /** a correction typed and not saved: stepping away asks first */
  onDirty: (dirty: boolean) => void;
}

export function CaseDetail(p: CaseDetailProps) {
  const { c, busy } = p;
  const write = p.canWrite !== false;
  // a worklist approves only the records of the scope on screen, and says how many (#286 S10)
  const scopedActions = c.actions.flatMap((a): CaseAction[] =>
    a.kind === 'approve' && c.members && c.approve
      ? c.approve.length
        ? [{ ...a, shiftIds: c.approve, label: T('Godkjenn alle {n}', { n: c.approve.length }) }]
        : []
      : [a],
  );
  const [first, second] = scopedActions.filter((a) => write || !WRITES.includes(a.kind));
  const clocked = c.members?.every((m) => m.hours != null)
    ? c.members.reduce((sum, m) => sum + (m.hours ?? 0), 0)
    : null;
  const style = { '--acc': TONE[c.tone], '--acc-ink': INK[c.tone] } as CSSProperties;
  const why = c.why ?? (c.description !== c.person?.name ? c.description : '');
  const when = c.dateText ?? c.dateLabel;
  const state = stateOf(c, { waiting: p.waiting, later: p.later });
  const field = first && (first.kind === 'correct-in' || first.kind === 'correct-out') && c.correct;
  return (
    <article className={css.case} style={style} data-testid="case-detail" data-tone={c.tone}>
      <div className={css.head}>
        <span className={css.sev} data-state={state.kind}>
          <span className={css.sevDot} aria-hidden="true" />
          {state.word}
        </span>
        <h2 className={css.what} ref={p.titleRef} tabIndex={-1} data-testid="case-title">
          {c.what ?? c.title}
        </h2>
      </div>
      <div className={css.ctx}>
        {c.person && (
          <button
            type="button"
            className={css.who}
            data-testid="review-ctx"
            disabled={busy}
            onClick={() => p.onPerson((c.person as { id: number }).id)}
          >
            <span className={css.av} aria-hidden="true">
              {initials(c.person.name)}
            </span>
            {c.person.name}
          </button>
        )}
        {c.date && when ? (
          <button
            type="button"
            className={`${css.when} ${c.person ? css.indent : ''}`}
            data-testid="review-ctx"
            disabled={busy}
            onClick={() => p.onDay(c.date as string)}
          >
            {when}
          </button>
        ) : (
          when && <span className={`${css.when} ${c.person ? css.indent : ''}`}>{when}</span>
        )}
        {c.shift && <Chip s={c.shift} className={c.person ? css.indent : ''} />}
        {p.outside && (
          <Tip text={T('Denne saken gjelder {d}, utenfor perioden du ser.', { d: c.dateLabel ?? '' })}>
            <button
              type="button"
              className={`${rb.badge} ${rb.past} ${rb.badgeBtn} ${c.person ? css.indent : ''}`}
              data-testid="review-offscreen"
              aria-label={T('Annen periode: {d}. Vis riktig periode', { d: c.dateLabel ?? '' })}
              disabled={busy}
              onClick={p.onShowPeriod}
            >
              {T('Annen periode')} <Icon name="chevronRight" size={14} />
            </button>
          </Tip>
        )}
      </div>
      {p.waiting && (
        <p className={css.wait} data-testid="review-waiting-note">
          {T(
            'Du venter på svar fra en ansatt. Du trenger ikke gjøre noe før svaret kommer, og saken telles ikke som arbeid.',
          )}
        </p>
      )}
      {p.later && p.laterUntil && (
        <p className={css.wait} data-testid="review-later-note">
          {T('Utsatt til {d}. Saken er ikke løst eller godkjent.', {
            d: p.laterUntil.split('-').reverse().join('.'),
          })}
        </p>
      )}
      {c.members?.length ? (
        <dl className={css.facts} data-testid="review-members">
          <div>
            <dt>{T('Registreringer i utvalget')}</dt>
            <dd>{c.members.length}</dd>
          </div>
          {c.elsewhere ? (
            <div>
              <dt>{T('I andre team')}</dt>
              <dd>{c.elsewhere}</dd>
            </div>
          ) : null}
          {clocked != null && (
            <div>
              <dt>{T('Stemplet')}</dt>
              <dd>{T('{h} t', { h: num(Math.round(clocked * 10) / 10) })}</dd>
            </div>
          )}
        </dl>
      ) : null}
      {c.facts.length > 0 && (
        // the decisive facts in words, each once: planned and registered times, the gap, who gets what
        <dl className={css.facts} data-testid="review-facts">
          {c.facts.map((f) => (
            <div key={`${f.label}-${f.value}`}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {c.fig && (
        <div className={css.fig} data-testid="case-figure" data-kind={c.fig.kind}>
          <Figure f={c.fig} />
        </div>
      )}
      {(c.outcome?.length || c.balance) && (
        <ul className={css.outcome} data-testid="case-outcome">
          {c.outcome?.slice(0, -1).map((x) => (
            <li key={x}>{x}</li>
          ))}
          {c.balance && <Balance b={c.balance} />}
          {c.outcome?.slice(-1).map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      {p.impact && <div className={rb.left}>{p.impact}</div>}
      {(why || p.about || c.law) && (
        <p className={css.why} data-testid="case-why">
          {why}
          {p.about ? <> {p.about}</> : null}
          {c.law && <span className={css.src}>{c.law}</span>}
        </p>
      )}
      {c.findings && c.findings.length > 1 && (
        <section className={css.findings} data-testid="case-findings">
          <h3>{T('Dette avklarer du sammen')}</h3>
          <ul>
            {c.findings.map((f) => (
              <li key={f}>
                <Icon name="info" size={16} strokeWidth={1.7} />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {c.finish && !p.waiting && (
        <p className={css.finish} data-testid="case-finish">
          <strong>{T('Når er oppgaven ferdig?')}</strong> {c.finish}
        </p>
      )}
      {p.extra}
      {busy && (
        <p className={rb.note} aria-live="polite" data-testid="review-saving">
          {T('Lagrer … Saken står til svaret har kommet.')}
        </p>
      )}
      {p.error && (
        <p className={rb.error} role="alert">
          <Icon name="warn" size={16} /> {p.error}
        </p>
      )}
      <div className={css.acts} data-testid="review-actions">
        {field ? (
          <CaseField
            key={`${c.key}-${first.kind}`}
            a={first}
            f={c.correct as CorrectOut}
            busy={busy}
            caseKey={c.key}
            onSubmit={p.onSubmit}
            onDirty={p.onDirty}
          />
        ) : (
          first && <Act a={first} className={css.pri} busy={busy} onAction={p.onAction} />
        )}
        {c.noPhone && (
          <p className={css.noPhone} data-testid="case-no-phone">
            <Icon name="phone" size={16} strokeWidth={1.7} />
            {T('Telefonnummer mangler')}
          </p>
        )}
        {(second || (!p.waiting && write)) && (
          <div className={css.row}>
            {second && <Act a={second} className={css.sec} busy={busy} onAction={p.onAction} />}
            {!p.waiting && write && (
              // postponing resolves nothing: the case moves to Utsatt, and back from there
              <button
                type="button"
                className={css.later}
                disabled={busy}
                onClick={p.onLater}
                data-testid="case-later"
              >
                {p.later ? T('Følg opp nå') : T('Ta senere')}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

/**
 * «Feriedager igjen: X nå, X+n hvis du godtar», only when the personnel record is readable to this
 * reader (GET /admin/users/:id); while it loads or when it is refused, nothing is said.
 */
function Balance({ b }: { b: NonNullable<ReviewCase['balance']> }) {
  const left = useUser(b.userId).data?.[b.field];
  if (left == null) return null;
  return (
    <li data-testid="case-balance">
      {T('Feriedager igjen: {now} nå, {after} hvis du godtar', { now: num(left), after: num(left + b.back) })}
    </li>
  );
}

/** A button, or for Ring and Send melding a tel: or sms: link. */
function Act({
  a,
  className,
  busy,
  onAction,
}: { a: CaseAction; className: string | undefined; busy: boolean; onAction: (a: CaseAction) => void }) {
  if (a.kind === 'contact')
    return (
      <a className={className} href={a.href} data-testid="case-contact">
        {a.label}
      </a>
    );
  return (
    <button type="button" className={className} disabled={busy} onClick={() => onAction(a)}>
      {a.label}
    </button>
  );
}

/**
 * The time a correction puts in, filled from the plan, and for a clock-out its reason (UC7): what is
 * typed is checked before anything is sent. The primary button sends it.
 */
function CaseField({
  a,
  f,
  busy,
  caseKey,
  onSubmit,
  onDirty,
}: {
  a: Correct;
  f: CorrectOut;
  busy: boolean;
  caseKey: string;
  onSubmit: (a: Correct, input: ActInput) => void;
  onDirty: (dirty: boolean) => void;
}) {
  const out = a.kind === 'correct-out';
  const from = oslo(out ? f.plannedEnd : f.plannedStart);
  const inAt = oslo(f.punchIn);
  // the actual end's date is said, never guessed: it starts on the clock-in's day (S05)
  const startDate = out ? (inAt?.date ?? from?.date ?? '') : (from?.date ?? '');
  // a draft kept in this tab: a look-up of the phone number and back, or a reload, loses nothing (S06)
  const saved = drafts.read(caseKey);
  const [date, setDate] = useState(saved?.date || startDate);
  const [time, setTime] = useState(saved?.time ?? '');
  const [reason, setReason] = useState(saved?.reason ?? '');
  const [problem, setProblem] = useState<{ time?: string; reason?: string }>({});
  const dirty = !!time || !!reason || date !== startDate;
  useEffect(
    () => drafts.write(caseKey, dirty ? { date, time, reason } : null),
    [caseKey, dirty, date, time, reason],
  );
  // typed and not saved: the page's own exits (a person, a day, a link) ask first
  useDirty('review-correct', dirty, T('Retting av tid'));
  useEffect(() => onDirty(dirty), [dirty, onDirty]);
  useEffect(() => () => onDirty(false), [onDirty]);
  const submit = () => {
    const x: { time?: string; reason?: string } = {};
    let at = '';
    if (!/^\d\d:\d\d$/.test(time))
      x.time = out ? T('Skriv inn faktisk sluttid.') : T('Skriv inn faktisk starttid.');
    else if (!/^\d{4}-\d\d-\d\d$/.test(date)) x.time = T('Velg en dato.');
    else if (out) {
      // in Oslo time, whatever the browser's own zone is; the end must come after the clock-in (S05)
      at = osloISO(date, time);
      if (!f.punchIn || Date.parse(at) <= Date.parse(f.punchIn))
        x.time = T(
          'Slutt må være etter innstemplingen kl. {t}. Velg neste dato hvis vakten sluttet etter midnatt.',
          {
            t: inAt?.time ?? '',
          },
        );
      else if (Date.parse(at) > Date.now()) x.time = T('Sluttiden kan ikke være senere enn nå.');
    } else {
      at = osloISO(date, time);
      if (Date.parse(at) > Date.now()) x.time = T('Starttiden kan ikke være senere enn nå.');
    }
    if (!reason) x.reason = T('Velg en begrunnelse.');
    setProblem(x);
    if (!x.time && !x.reason) onSubmit(a, { at, reason, approve: false });
  };
  return (
    <div className={css.fld} data-testid="review-form">
      {out && (
        <label>
          <span>{T('Faktisk sluttdato')}</span>
          <input
            type="date"
            value={date}
            min={startDate}
            onChange={(e) => setDate(e.target.value)}
            data-testid="review-form-date"
          />
          <small className={css.hint}>{T('Velg neste dato hvis vakten sluttet etter midnatt.')}</small>
        </label>
      )}
      <label>
        <span>{out ? T('Faktisk sluttid') : T('Faktisk starttid')}</span>
        <input
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          aria-invalid={problem.time ? true : undefined}
          aria-describedby={problem.time ? 'case-time-err' : undefined}
          data-testid="review-form-time"
        />
        <small className={css.hint}>
          {T('Planlagt {t}. Bruk avklart tid, ikke bare tiden i planen.', { t: from?.time ?? '' })}
        </small>
      </label>
      {problem.time && (
        <small id="case-time-err" className={rb.fieldError} aria-live="assertive">
          {problem.time}
        </small>
      )}
      <label>
        <span>{T('Begrunnelse')}</span>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-invalid={problem.reason ? true : undefined}
          aria-describedby={problem.reason ? 'case-reason-err' : undefined}
          data-testid="review-form-reason"
        >
          <option value="">{T('Velg en begrunnelse')}</option>
          {CORRECTION_REASONS.map((r) => (
            <option key={r} value={r}>
              {T(r)}
            </option>
          ))}
        </select>
      </label>
      {problem.reason && (
        <small id="case-reason-err" className={rb.fieldError} aria-live="assertive">
          {problem.reason}
        </small>
      )}
      <button
        type="button"
        className={css.pri}
        disabled={busy}
        onClick={submit}
        data-testid="review-form-save"
      >
        {busy ? T('Lagrer …') : out ? T('Lagre utstempling') : T('Lagre innstempling')}
      </button>
    </div>
  );
}

function Chip({ s, className = '' }: { s: CaseShift; className?: string | undefined }) {
  return (
    <span className={`${css.chip} ${className}`}>
      <span className={css.chipDot} aria-hidden="true" style={{ background: s.color || 'var(--neu-l)' }} />
      {s.type ? `${s.time} ${s.type}` : s.time}
    </span>
  );
}

// ---- the figures: labels above, legends under, nothing beside ----

const H = 36e5;
const clamp = (x: number) => Math.min(100, Math.max(0, x));
/** a position on an axis from `a` to `b` hours, as a percentage */
const axis = (a: number, b: number) => (h: number) => clamp(((h - a) / (b - a)) * 100);
const two = (n: number) => String(((Math.floor(n) % 24) + 24) % 24).padStart(2, '0');
const hh = (h: number) => `${two(h)}:${String(Math.round((h % 1) * 60) % 60).padStart(2, '0')}`;

function Seg({
  at,
  from,
  to,
  cls,
  color,
}: { at: (h: number) => number; from: number; to: number; cls: string | undefined; color?: string | null }) {
  const left = at(from);
  const width = at(to) - left;
  if (width <= 0) return null;
  return (
    <div
      className={`${css.seg} ${cls}`}
      style={{ left: `${left}%`, width: `${width}%`, ...(color ? { background: color } : {}) }}
    />
  );
}
const Mark = ({ at, h }: { at: (h: number) => number; h: number }) => (
  <div className={css.mark} style={{ left: `${at(h)}%` }} />
);
function Lane({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={css.lane}>
      <span>{label}</span>
      <div className={css.track}>{children}</div>
    </div>
  );
}
function Ticks({ at, list }: { at: (h: number) => number; list: [number, string][] }) {
  return (
    <div className={css.ticks} aria-hidden="true">
      {list.map(([h, l]) => (
        <em key={`${h}-${l}`} style={{ left: `${at(h)}%` }}>
          {l}
        </em>
      ))}
    </div>
  );
}
/** whole hours every `step`, the first and last always */
function hourTicks(a: number, b: number, step: number): [number, string][] {
  const out: [number, string][] = [[a, two(Math.ceil(a))]];
  for (let h = Math.ceil(a / step) * step; h < b; h += step)
    if (h - a >= step / 2 && b - h >= step / 2) out.push([h, two(h)]);
  out.push([b, two(Math.floor(b))]);
  return out;
}

function Figure({ f }: { f: CaseFigure }) {
  return (
    <>
      {f.big && <div className={css.big}>{f.big}</div>}
      {f.legend && <div className={css.legend}>{f.legend}</div>}
      <FigureBody f={f} />
    </>
  );
}

function FigureBody({ f }: { f: CaseFigure }) {
  switch (f.kind) {
    case 'rest': {
      const mid = Date.parse(osloISO(oslo(f.one.start)?.date ?? '', '00:00'));
      const h = (iso: string) => (Date.parse(iso) - mid) / H;
      const e1 = h(f.one.end);
      const s2 = h(f.two.start);
      const a = h(f.one.start) - 1;
      const b = h(f.two.end) + 1;
      const at = axis(a, b);
      return (
        <>
          <div className={css.lanes}>
            <Lane label={`${f.one.day ?? ''} ${f.one.time}`.trim()}>
              <Seg at={at} from={h(f.one.start)} to={e1} cls={css.type} color={f.one.color} />
            </Lane>
            <Lane label={T('Hvile')}>
              <Seg at={at} from={e1} to={s2} cls={css.acc} />
              <Mark at={at} h={e1 + f.need} />
            </Lane>
            <Lane label={`${f.two.day ?? ''} ${f.two.time}`.trim()}>
              <Seg at={at} from={s2} to={h(f.two.end)} cls={css.type} color={f.two.color} />
            </Lane>
          </div>
          <Ticks
            at={at}
            list={[
              [a, hh(a)],
              [e1, hh(e1)],
              [s2, hh(s2)],
              [b, hh(b)],
            ]}
          />
        </>
      );
    }
    case 'meter': {
      const max = Math.ceil(Math.max(f.value, f.limit) * 1.15);
      const at = axis(0, max);
      return (
        <div className={css.meter}>
          <div className={css.fill} style={{ width: `${at(Math.min(f.value, f.limit))}%` }} />
          {f.value > f.limit && (
            <div
              className={css.over}
              style={{ left: `${at(f.limit)}%`, width: `${at(f.value) - at(f.limit)}%` }}
            />
          )}
          {f.marks.map((m) => (
            <div
              key={m.at}
              className={`${css.m} ${m.at / max > 0.85 ? css.end : ''}`}
              style={{ left: `${at(m.at)}%` }}
            >
              <em>{m.label}</em>
            </div>
          ))}
        </div>
      );
    }
    case 'need': {
      const max = Math.max(1, ...f.cols.map((c) => Math.max(c.need, c.have)));
      const first = f.cols[0]?.h ?? 0;
      const last = (f.cols.at(-1)?.h ?? 0) + 1;
      const needs = f.cols.map((c) => c.need);
      const lo = Math.min(...needs);
      const hi = Math.max(...needs);
      const at = axis(first, last);
      return (
        <>
          <div className={css.cols}>
            {f.cols.map((c) => (
              <div key={c.h} title={T('{t}: {n} av {m}', { t: `${two(c.h)}:00`, n: c.have, m: c.need })}>
                <b style={{ height: `${(Math.min(c.have, c.need) / max) * 100}%` }} />
                <s style={{ height: `${(Math.max(0, c.need - c.have) / max) * 100}%` }} />
              </div>
            ))}
          </div>
          <Ticks at={at} list={hourTicks(first, last, 6)} />
          <div className={css.legend}>
            <span>
              <i className={css.keyPlanned} />
              {T('Planlagt')}
            </span>
            <span>
              <i className={css.keyShort} />
              {T('Mangler')}
            </span>
            <span>
              {lo === hi
                ? T('Behovet er {n} på jobb', { n: num(lo) })
                : T('Behovet er {a} til {b} på jobb', { a: num(lo), b: num(hi) })}
            </span>
          </div>
        </>
      );
    }
    case 'youth': {
      const a = Math.min(15, Math.floor(f.from));
      const b = Math.max(31, Math.ceil(f.to));
      const at = axis(a, b);
      return (
        <>
          <div className={css.lanes}>
            <Lane label={T('Vakt {t}', { t: f.shift.time })}>
              <Seg at={at} from={f.nightFrom} to={30} cls={css.soft} />
              {f.eveningFrom != null && <Seg at={at} from={f.eveningFrom} to={f.nightFrom} cls={css.tint} />}
              <Seg at={at} from={f.from} to={f.to} cls={css.type} color={f.shift.color} />
              <Seg at={at} from={Math.max(f.from, f.nightFrom)} to={Math.min(f.to, 30)} cls={css.acc} />
            </Lane>
          </div>
          <Ticks at={at} list={[a, a + 4, a + 8, a + 12, b].map((h): [number, string] => [h, two(h)])} />
          <div className={css.legend}>
            <span>
              <i className={css.keyNight} />
              {T('Ikke lov {from}–06', { from: f.nightFrom })}
            </span>
            {f.eveningFrom != null && (
              <span>
                <i className={css.keyEvening} />
                {T('{from}–{to} bare når arbeidet krever det', { from: f.eveningFrom, to: f.nightFrom })}
              </span>
            )}
          </div>
        </>
      );
    }
  }
}
